<template>
  <div class="san-gao-pay-page">
    <header class="pay-hero">
      <h1>三高修复·健康重启</h1>
      <p>专业治疗·长期管理</p>
    </header>

    <main class="pay-content">
      <section v-if="order" class="summary-card">
        <div class="security-line"><span>✓</span> 客户资料仅在服务端保存，未包含在分享链接中</div>
        <h2>{{ order.projectName }}</h2>
        <div class="summary-row">
          <span>权益人</span>
          <strong>{{ order.beneficiaryNames || "到会嘉宾" }}</strong>
        </div>
        <div class="divider"></div>
        <div v-for="item in order.itemList" :key="item.productName" class="product-row">
          <div>
            <strong>{{ item.productName }}</strong>
            <small>¥{{ formatAmount(item.productPrice) }} × {{ item.productCount }}</small>
          </div>
          <b>¥{{ formatAmount(item.productPrice * item.productCount) }}</b>
        </div>
        <div class="total-row">
          <span>待支付</span>
          <strong><em>¥</em>{{ formatAmount(order.totalPrice) }}</strong>
        </div>
      </section>

      <section v-else-if="errorMessage" class="state-card">
        <div class="state-icon">!</div>
        <h2>无法打开代付单</h2>
        <p>{{ errorMessage }}</p>
      </section>

      <section v-else class="state-card">
        <van-loading color="#1989d8" size="32" vertical>正在加载代付单...</van-loading>
      </section>

      <p v-if="order" class="pay-notice">
        付款成功后，项目权益将发放给上方所示权益人，不会发放给代付人。
      </p>
    </main>

    <footer v-if="order" class="pay-footer">
      <div><small>合计</small><strong>¥{{ formatAmount(order.totalPrice) }}</strong></div>
      <button type="button" :disabled="loading" @click="pay">
        {{ loading ? "正在唤起支付..." : "确认代付" }}
      </button>
    </footer>
  </div>
</template>

<script>
import { Toast } from "vant";
import { getSanGaoPayShare, paySanGaoShare } from "@/api";
import { weixinInit } from "@/utils/wechatUtil.js";
import sanGaoShareImage from "@/assets/sanGao/share-card.jpg";

export default {
  name: "SanGaoPay",
  data() {
    return {
      shareToken: "",
      order: null,
      errorMessage: "",
      loading: false,
      lastPayParams: null,
    };
  },
  created() {
    this.shareToken = this.$route.query.shareToken || "";
    this.initWechat();
    this.loadOrder();
  },
  methods: {
    initWechat() {
      weixinInit({
        publicType: this.$route.query.publicType || localStorage.getItem("publicType") || 2,
        url: window.location.href.split("#")[0],
        title: "三高修复·健康重启",
        desc: "专业治疗·长期管理",
        imgUrl: new URL(sanGaoShareImage, window.location.origin).href,
      });
    },
    async loadOrder() {
      if (!/^[a-fA-F0-9]{32}$/.test(this.shareToken)) {
        this.errorMessage = "代付链接无效";
        return;
      }
      try {
        const response = await getSanGaoPayShare({ shareToken: this.shareToken });
        if (response.code === 200) {
          this.order = response.data;
        } else {
          this.errorMessage = response.msg || "代付单不存在、已完成或已过期";
        }
      } catch (error) {
        this.errorMessage = "网络异常，请稍后重试";
      }
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    async pay() {
      if (this.loading) return;
      if (this.lastPayParams) {
        this.loading = true;
        this.invokeWechatPay(this.lastPayParams);
        return;
      }
      this.loading = true;
      try {
        const response = await paySanGaoShare({
          shareToken: this.shareToken,
          wxCode: localStorage.getItem("code") || "",
        });
        if (response.code === 200) {
          this.lastPayParams = response.data;
          this.invokeWechatPay(response.data);
        } else {
          this.loading = false;
          Toast(response.msg || "唤起支付失败");
        }
      } catch (error) {
        this.loading = false;
        Toast("网络异常，请稍后重试");
      }
    },
    invokeWechatPay(payParams) {
      if (!payParams) {
        this.loading = false;
        Toast("未获取到支付信息");
        return;
      }
      const invoke = () => {
        window.WeixinJSBridge.invoke(
          "getBrandWCPayRequest",
          {
            appId: payParams.appId,
            timeStamp: payParams.timeStamp,
            nonceStr: payParams.nonceStr,
            package: payParams.packages,
            signType: payParams.signType,
            paySign: payParams.paySign,
          },
          result => {
            this.loading = false;
            if (result.err_msg === "get_brand_wcpay_request:ok") {
              const successPath = this.order.productMark === 44
                ? "/sanGaoDaoHuiLiInfoSuccess"
                : "/sanGaoInfoSuccess";
              window.location.href = `${window.location.origin}${successPath}?payNo=${payParams.payNo}`;
            } else if (result.err_msg === "get_brand_wcpay_request:cancel") {
              Toast("已取消支付，可再次点击继续支付");
            } else {
              this.lastPayParams = null;
              Toast("支付未完成，请重试");
            }
          }
        );
      };
      if (typeof window.WeixinJSBridge === "undefined") {
        document.addEventListener("WeixinJSBridgeReady", invoke, false);
      } else {
        invoke();
      }
    },
  },
};
</script>

<style lang="less" scoped>
* { box-sizing: border-box; }

.san-gao-pay-page {
  position: relative;
  min-height: 100vh;
  padding-bottom: 104px;
  overflow: hidden;
  font-size: 14px;
  color: #173d5b;
  background: #0874ca;
}

.san-gao-pay-page::before,
.san-gao-pay-page::after {
  position: fixed;
  inset: 0;
  content: "";
  pointer-events: none;
}

.san-gao-pay-page::before {
  background: url("../../assets/sanGao/background.jpg") center -86px / cover no-repeat;
  opacity: .5;
}

.san-gao-pay-page::after {
  background: linear-gradient(180deg, rgba(0, 75, 164, .1) 0%, rgba(196, 235, 255, .58) 38%, rgba(224, 247, 255, .9) 58%, #dff6ff 100%);
}

.pay-hero,
.pay-content,
.pay-footer { position: relative; z-index: 1; }

.pay-hero {
  height: 270px;
  padding: 76px 24px 0;
  color: #fff;
  text-shadow: 0 3px 16px rgba(0, 48, 113, .45);
}

.pay-hero h1 {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.28;
  letter-spacing: 2.5px;
}

.pay-hero p {
  margin: 14px 0 0;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 3px;
  opacity: .94;
}
.pay-content { padding: 0 16px; }

.summary-card,
.state-card {
  padding: 20px 17px;
  border: 1px solid rgba(255,255,255,.75);
  border-radius: 18px;
  background: rgba(255,255,255,.95);
  box-shadow: 0 12px 30px rgba(11, 87, 145, .14);
}

.security-line {
  margin: -2px 0 17px;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  color: #2b8967;
  background: #ecfaf4;
}

.security-line span { margin-right: 4px; font-weight: 700; }
.summary-card h2 { margin: 0 0 18px; font-size: 18px; line-height: 1.45; }
.summary-row,
.product-row,
.total-row { display: flex; align-items: center; justify-content: space-between; }
.summary-row { font-size: 13px; color: #7893a6; }
.summary-row strong { color: #284b64; }
.divider { height: 1px; margin: 18px 0 4px; background: #e4eff5; }
.product-row { min-height: 66px; border-bottom: 1px solid #e8f1f6; }
.product-row div { display: flex; flex-direction: column; }
.product-row strong { margin-bottom: 5px; font-size: 14px; }
.product-row small { font-size: 12px; color: #8ba2b2; }
.product-row b { font-size: 14px; color: #e77b31; }
.total-row { padding-top: 19px; }
.total-row span { font-size: 13px; color: #6d899c; }
.total-row strong { font-size: 24px; color: #e8752d; }
.total-row em { font-size: 14px; font-style: normal; }
.pay-notice { margin: 15px 6px; font-size: 12px; line-height: 1.65; color: #65889e; }
.state-card { min-height: 190px; display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.state-icon { width: 48px; height: 48px; line-height: 48px; border-radius: 50%; font-size: 25px; color: #fff; background: #8eabc0; }
.state-card h2 { margin: 14px 0 8px; font-size: 18px; }
.state-card p { margin: 0; font-size: 13px; color: #7893a6; }

.pay-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 84px;
  padding: 12px 16px 22px;
  border-radius: 18px 18px 0 0;
  background: rgba(255,255,255,.98);
  box-shadow: 0 -8px 24px rgba(18, 82, 126, .12);
}

.pay-footer div { display: flex; flex-direction: column; }
.pay-footer small { font-size: 12px; color: #849dac; }
.pay-footer strong { margin-top: 2px; font-size: 20px; color: #e8752d; }
.pay-footer button { height: 44px; min-width: 150px; border: 0; border-radius: 22px; font-size: 15px; font-weight: 600; color: #fff; background: linear-gradient(100deg, #19a2e5, #146bd2); box-shadow: 0 6px 15px rgba(17, 112, 199, .25); }
.pay-footer button:disabled { opacity: .65; }
</style>
